import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, Search, Filter, RefreshCw, Calendar, DollarSign } from 'lucide-react';

const AllBillsPage = () => {
  const navigate = useNavigate();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortField, setSortField] = useState('due_date');
  const [sortDirection, setSortDirection] = useState('asc');

  const apiUrl = typeof window !== 'undefined' ? window.location.origin : '';

  useEffect(() => {
    fetchBills();
  }, []);

  const fetchBills = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${apiUrl}/api/excel/get-bills`);
      const data = await response.json();
      
      if (data.status === 'success' && data.bills) {
        setBills(data.bills);
      }
    } catch (error) {
      console.error('Error fetching bills:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  // Filter and sort bills
  const filteredBills = bills
    .filter(bill => {
      const matchesSearch = (bill.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                           (bill.category || '').toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || bill.status === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];
      
      if (sortField === 'amount') {
        aVal = parseFloat(aVal) || 0;
        bVal = parseFloat(bVal) || 0;
      } else if (sortField === 'due_date') {
        aVal = new Date(aVal || '9999-12-31');
        bVal = new Date(bVal || '9999-12-31');
      } else {
        aVal = (aVal || '').toString().toLowerCase();
        bVal = (bVal || '').toString().toLowerCase();
      }
      
      if (sortDirection === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      paid: 'bg-green-500/20 text-green-400 border-green-500/30',
      upcoming: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
      overdue: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return styles[status] || styles.upcoming;
  };

  const totalAmount = filteredBills.reduce((sum, bill) => sum + (bill.amount || 0), 0);
  const paidAmount = filteredBills.filter(b => b.status === 'paid').reduce((sum, bill) => sum + (bill.amount || 0), 0);
  const pendingAmount = filteredBills.filter(b => b.status !== 'paid').reduce((sum, bill) => sum + (bill.amount || 0), 0);

  return (
    <div className="min-h-screen bg-black text-white p-4 md:p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button 
            onClick={() => navigate('/finances-re')}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <ArrowLeft size={24} />
          </button>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold">All Bills</h1>
            <p className="text-gray-400 text-sm">Manage and track all your bills</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button 
            onClick={fetchBills}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw size={20} className={loading ? 'animate-spin' : ''} />
          </button>
          <button className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 px-4 py-2 rounded-lg transition-colors">
            <Download size={18} />
            Export
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-500/20 rounded-lg">
              <DollarSign size={20} className="text-blue-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Total Bills</p>
              <p className="text-xl font-bold">{formatCurrency(totalAmount)}</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <DollarSign size={20} className="text-green-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Paid</p>
              <p className="text-xl font-bold text-green-400">{formatCurrency(paidAmount)}</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-900 border border-gray-800 rounded-xl p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-500/20 rounded-lg">
              <DollarSign size={20} className="text-yellow-400" />
            </div>
            <div>
              <p className="text-gray-400 text-sm">Pending</p>
              <p className="text-xl font-bold text-yellow-400">{formatCurrency(pendingAmount)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search bills..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-10 pr-4 py-2.5 text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
          />
        </div>
        <div className="flex gap-2">
          {['all', 'upcoming', 'paid', 'overdue'].map((status) => (
            <button
              key={status}
              onClick={() => setStatusFilter(status)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                statusFilter === status
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
              }`}
            >
              {status.charAt(0).toUpperCase() + status.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-800 bg-gray-900/50">
                <th 
                  className="text-left p-4 text-gray-400 font-medium cursor-pointer hover:text-white"
                  onClick={() => handleSort('name')}
                >
                  <div className="flex items-center gap-2">
                    Bill Name
                    {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                </th>
                <th 
                  className="text-left p-4 text-gray-400 font-medium cursor-pointer hover:text-white"
                  onClick={() => handleSort('category')}
                >
                  <div className="flex items-center gap-2">
                    Category
                    {sortField === 'category' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                </th>
                <th 
                  className="text-left p-4 text-gray-400 font-medium cursor-pointer hover:text-white"
                  onClick={() => handleSort('due_date')}
                >
                  <div className="flex items-center gap-2">
                    Due Date
                    {sortField === 'due_date' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                </th>
                <th 
                  className="text-right p-4 text-gray-400 font-medium cursor-pointer hover:text-white"
                  onClick={() => handleSort('amount')}
                >
                  <div className="flex items-center justify-end gap-2">
                    Amount
                    {sortField === 'amount' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </div>
                </th>
                <th className="text-center p-4 text-gray-400 font-medium">Status</th>
                <th className="text-left p-4 text-gray-400 font-medium">Notes</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-gray-500">
                    <RefreshCw size={24} className="animate-spin mx-auto mb-2" />
                    Loading bills...
                  </td>
                </tr>
              ) : filteredBills.length === 0 ? (
                <tr>
                  <td colSpan={6} className="p-8 text-center text-gray-500">
                    No bills found
                  </td>
                </tr>
              ) : (
                filteredBills.map((bill, idx) => (
                  <tr 
                    key={bill.id || idx} 
                    className="border-b border-gray-800 hover:bg-gray-800/50 transition-colors"
                  >
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{bill.icon || '📄'}</span>
                        <span className="font-medium">{bill.name || '-'}</span>
                      </div>
                    </td>
                    <td className="p-4 text-gray-400">{bill.category || '-'}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-2 text-gray-300">
                        <Calendar size={14} className="text-gray-500" />
                        {formatDate(bill.due_date)}
                      </div>
                    </td>
                    <td className="p-4 text-right font-semibold">{formatCurrency(bill.amount)}</td>
                    <td className="p-4 text-center">
                      <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusBadge(bill.status)}`}>
                        {(bill.status || 'upcoming').charAt(0).toUpperCase() + (bill.status || 'upcoming').slice(1)}
                      </span>
                    </td>
                    <td className="p-4 text-gray-400 text-sm max-w-xs truncate">{bill.notes || '-'}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        
        {/* Footer */}
        <div className="p-4 border-t border-gray-800 bg-gray-900/50 flex items-center justify-between">
          <p className="text-gray-400 text-sm">
            Showing {filteredBills.length} of {bills.length} bills
          </p>
          <p className="text-gray-400 text-sm">
            Total: <span className="text-white font-semibold">{formatCurrency(totalAmount)}</span>
          </p>
        </div>
      </div>
    </div>
  );
};

export default AllBillsPage;
